import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { psychiatryAPI } from '../../utils/api';

const RISK_COLOR = {
    'High': { bg: '#fef2f2', color: '#dc2626', dot: '#dc2626' },
    'Medium': { bg: '#fff7ed', color: '#ea580c', dot: '#f97316' },
    'Low': { bg: '#f0fdf4', color: '#16a34a', dot: '#22c55e' },
};

export default function PsychCaseload() {
    const [cases, setCases] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, high_risk: 0, sessions_today: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ status: 'Active', risk_level: '' });
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const [cRes, sRes] = await Promise.all([
                psychiatryAPI.getCases(filter),
                psychiatryAPI.getStats(),
            ]);
            setCases(cRes.data);
            setStats(sRes.data);
        } catch { setCases([]); } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [filter]);

    const statCard = (label, value, color, icon) => (
        <div style={{ background: 'white', borderRadius: 10, border: `1px solid #e2e8f0`, padding: '16px 20px', flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{icon} {label}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color, marginTop: 4 }}>{value}</div>
        </div>
    );

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', margin: 0 }}>🧠 Psychiatry Caseload</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Patient cases & mental health management</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => navigate('/psych/supervision')}
                        style={{ padding: '8px 16px', background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#7c3aed' }}>
                        📋 Supervision Log
                    </button>
                    <button onClick={() => navigate('/psych/new')}
                        style={{ padding: '8px 18px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                        + New Case
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                {statCard('Total Cases', stats.total, '#7c3aed', '📁')}
                {statCard('Active', stats.active, '#059669', '✅')}
                {statCard('High Risk', stats.high_risk, '#dc2626', '⚠️')}
                {statCard("Today's Sessions", stats.sessions_today, '#2563eb', '🗓️')}
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['Active', 'Completed', 'On Hold', ''].map(s => (
                    <button key={s} onClick={() => setFilter(f => ({ ...f, status: s }))}
                        style={{ padding: '6px 16px', borderRadius: 20, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12,
                            fontWeight: filter.status === s ? 700 : 400,
                            background: filter.status === s ? '#7c3aed' : 'white',
                            color: filter.status === s ? 'white' : '#374151' }}>
                        {s || 'All'}
                    </button>
                ))}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    {['', 'High', 'Medium', 'Low'].map(r => (
                        <button key={r} onClick={() => setFilter(f => ({ ...f, risk_level: r }))}
                            style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12,
                                fontWeight: filter.risk_level === r ? 700 : 400,
                                background: filter.risk_level === r ? (RISK_COLOR[r]?.color || '#374151') : 'white',
                                color: filter.risk_level === r ? 'white' : '#374151' }}>
                            {r || 'All Risk'}
                        </button>
                    ))}
                    <button onClick={load} style={{ padding: '6px 12px', borderRadius: 20, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12, background: 'white' }}>🔄</button>
                </div>
            </div>

            {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
            : cases.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🧠</div>
                    <p style={{ color: '#64748b' }}>No cases found</p>
                    <button onClick={() => navigate('/psych/new')}
                        style={{ marginTop: 12, padding: '8px 20px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600 }}>
                        Register First Case
                    </button>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                {['Case No.', 'Patient', 'Counsellor', 'Diagnosis', 'Risk', 'Sessions', 'Status', 'Action'].map(h => (
                                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {cases.map(c => {
                                const rc = RISK_COLOR[c.risk_level] || RISK_COLOR.Low;
                                return (
                                    <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{c.case_number}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>{c.full_name}</div>
                                            <div style={{ fontSize: 11, color: '#64748b' }}>{c.mrn} | {c.age_years}y {c.gender}</div>
                                        </td>
                                        <td style={{ padding: '10px 14px', fontSize: 12 }}>{c.counsellor_name || '—'}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12, maxWidth: 180 }}>
                                            <div>{c.diagnosis || '—'}</div>
                                            {c.icd10_code && <div style={{ fontSize: 10, color: '#7c3aed' }}>{c.icd10_code}</div>}
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ background: rc.bg, color: rc.color, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                                                {c.risk_level}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700, color: '#374151', textAlign: 'center' }}>{c.session_count || 0}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ background: c.status === 'Active' ? '#dcfce7' : '#f1f5f9', color: c.status === 'Active' ? '#16a34a' : '#64748b', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                                                {c.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <button onClick={() => navigate(`/psych/cases/${c.id}`)}
                                                style={{ padding: '6px 14px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                Open
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
